import { supabase } from './supabase.js';

export const globalDeadlinesService = {
  // Get all global deadlines (single-family architecture)
  async getGlobalDeadlines() {
    try {
      // Get today's date for filtering
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];
      
      const { data, error } = await supabase
        .from('college_journey_global_deadlines')
        .select('*')
        .gte('due_date', todayStr) // Only get future deadlines
        // family_id filter removed for single-family architecture
        .order('due_date', { ascending: true });

      if (error) {
        console.warn('⚠️ Database error fetching global deadlines:', error.message);
        // Return static defaults if database fails
        return this.getStaticGlobalDeadlines();
      }

      // If no data returned, provide static defaults
      if (!data || data.length === 0) {
        console.log('ℹ️ No global deadlines found, returning static defaults');
        return this.getStaticGlobalDeadlines();
      }

      return data;
    } catch (error) {
      console.error('Error fetching global deadlines:', error);
      // Always provide fallback data
      return this.getStaticGlobalDeadlines();
    }
  },

  // Static fallback deadlines that always work
  getStaticGlobalDeadlines() {
    // Calculate proper dates based on current date and academic cycle
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    const today = new Date(currentYear, currentMonth, now.getDate());
    
    // Determine the academic year we're targeting
    // If it's past November, target next year's application cycle
    let applicationYear = currentYear;
    if (currentMonth >= 10) { // November or later
      applicationYear = currentYear + 1;
    }
    
    // Calculate FAFSA year
    let fafsaYear = applicationYear;
    
    const deadlines = [];
    
    // Only add future SAT dates
    const satDates = [
      { month: 7, day: 8, regDay: -15, test: 'August SAT' },     // Aug 8 registration, Aug 23 test
      { month: 7, day: 23, regDay: 0, test: 'August SAT' },
      { month: 8, day: 29, regDay: -15, test: 'September SAT' }, // Aug 29 registration, Sep 13 test  
      { month: 8, day: 13, regDay: 0, test: 'September SAT' },
      { month: 9, day: 19, regDay: -15, test: 'October SAT' },   // Sep 19 registration, Oct 4 test
      { month: 9, day: 4, regDay: 0, test: 'October SAT' },
      { month: 10, day: 21, regDay: -15, test: 'November SAT' }, // Oct 21 registration, Nov 5 test
      { month: 10, day: 5, regDay: 0, test: 'November SAT' },
      { month: 11, day: 18, regDay: -15, test: 'December SAT' }, // Nov 18 registration, Dec 3 test
      { month: 11, day: 3, regDay: 0, test: 'December SAT' }
    ];
    
    satDates.forEach((sat, index) => {
      const year = sat.month < 7 ? applicationYear + 1 : applicationYear;
      const satDate = new Date(year, sat.month, sat.day);
      
      // Only include if the date is in the future
      if (satDate > today) {
        if (sat.regDay < 0) {
          // Registration deadline
          deadlines.push({
            id: `static-sat-reg-${index}`,
            title: `${sat.test} Registration Deadline`,
            description: `Last day to register for ${sat.test}`,
            due_date: `${year}-${String(sat.month + 1).padStart(2, '0')}-${String(sat.day).padStart(2, '0')}`,
            category: 'testing',
            deadline_type: 'sat_registration',
            is_static: true
          });
        } else {
          // Test date
          deadlines.push({
            id: `static-sat-test-${index}`,
            title: sat.test,
            description: `${sat.test} Date`,
            due_date: `${year}-${String(sat.month + 1).padStart(2, '0')}-${String(sat.day).padStart(2, '0')}`,
            category: 'testing',
            deadline_type: 'sat_test',
            is_static: true
          });
        }
      }
    });
    
    // FAFSA dates (only if in future)
    const fafsaOpenDate = new Date(fafsaYear, 9, 1); // October 1
    if (fafsaOpenDate > today) {
      deadlines.push({
        id: 'static-fafsa-opens',
        title: 'FAFSA Opens',
        description: 'Federal financial aid application becomes available',
        due_date: `${fafsaYear}-10-01`,
        category: 'financial_aid',
        deadline_type: 'fafsa_open',
        is_static: true
      });
    }
    
    // Application deadlines (only if in future)
    const earlyDeadlineDate = new Date(applicationYear, 10, 1); // November 1
    if (earlyDeadlineDate > today) {
      deadlines.push(
        {
          id: 'static-early-action',
          title: 'Early Action Applications Due',
          description: 'Most Early Action college applications due',
          due_date: `${applicationYear}-11-01`,
          category: 'applications',
          deadline_type: 'early_action',
          is_static: true
        },
        {
          id: 'static-early-decision',
          title: 'Early Decision Applications Due',
          description: 'Most Early Decision college applications due',
          due_date: `${applicationYear}-11-01`,
          category: 'applications',
          deadline_type: 'early_decision',
          is_static: true
        }
      );
    }
    
    const regularDeadlineDate = new Date(applicationYear + 1, 0, 1); // January 1
    if (regularDeadlineDate > today) {
      deadlines.push({
        id: 'static-regular-decision',
        title: 'Regular Decision Applications Due',
        description: 'Most Regular Decision college applications due',
        due_date: `${applicationYear + 1}-01-01`,
        category: 'applications',
        deadline_type: 'regular_decision',
        is_static: true
      });
    }
    
    const fafsaPriorityDate = new Date(fafsaYear + 1, 1, 15); // February 15
    if (fafsaPriorityDate > today) {
      deadlines.push({
        id: 'static-fafsa-priority',
        title: 'FAFSA Priority Deadline',
        description: 'Submit FAFSA by this date for maximum financial aid consideration',
        due_date: `${fafsaYear + 1}-02-15`,
        category: 'financial_aid',
        deadline_type: 'fafsa_priority',
        is_static: true
      });
    }
    
    return deadlines;
  },

  // Create a new global deadline (single-family architecture)
  async createGlobalDeadline(deadline, userId) {
    try {
      const { data, error } = await supabase
        .from('college_journey_global_deadlines')
        .insert({
          ...deadline,
          
          user_id: userId
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating global deadline:', error);
      throw error;
    }
  },

  // Update a global deadline
  async updateGlobalDeadline(deadlineId, updates) {
    try {
      const { data, error } = await supabase
        .from('college_journey_global_deadlines')
        .update(updates)
        .eq('id', deadlineId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating global deadline:', error);
      throw error;
    }
  },

  // Delete a global deadline
  async deleteGlobalDeadline(deadlineId) {
    try {
      const { error } = await supabase
        .from('college_journey_global_deadlines')
        .delete()
        .eq('id', deadlineId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting global deadline:', error);
      throw error;
    }
  },

  // Subscribe to global deadlines changes
  subscribeToGlobalDeadlines(familyId, callback) {
    const subscription = supabase
      .channel('global_deadlines_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'college_journey_global_deadlines',
          // family_id filter removed for single-family architecture
        },
        callback
      )
      .subscribe();

    return subscription;
  },

  // Seed default global deadlines for a family
  async seedDefaultGlobalDeadlines(familyId, userId) {
    // Skip seeding if user ID is missing or invalid
    if (!userId || typeof userId !== 'string') {
      console.warn('⚠️ Skipping global deadlines seeding - invalid user context:', userId);
      return [];
    }

    const defaultDeadlines = [
      {
        title: 'FAFSA Opens',
        description: 'Federal financial aid application becomes available',
        due_date: '2025-07-01',
        category: 'financial_aid',
        deadline_type: 'fafsa_open'
      },
      {
        title: 'SAT Registration Deadline',
        description: 'Last day to register for August SAT',
        due_date: '2025-08-08',
        category: 'testing',
        deadline_type: 'sat_registration'
      },
      {
        title: 'August SAT',
        description: 'August SAT Date',
        due_date: '2025-08-23',
        category: 'testing',
        deadline_type: 'sat_test'
      },
      {
        title: 'SAT Registration Deadline', 
        description: 'Last day to register for September SAT',
        due_date: '2025-08-29',
        category: 'testing',
        deadline_type: 'sat_registration'
      },
      {
        title: 'September SAT',
        description: 'September SAT Date',
        due_date: '2025-09-13',
        category: 'testing',
        deadline_type: 'sat_test'
      },
      {
        title: 'SAT Registration Deadline',
        description: 'Last day to register for October SAT',
        due_date: '2025-09-19',
        category: 'testing',
        deadline_type: 'sat_registration'
      },
      {
        title: 'October SAT',
        description: 'October SAT Date',
        due_date: '2025-10-04',
        category: 'testing',
        deadline_type: 'sat_test'
      },
      {
        title: 'FAFSA Closes',
        description: 'Federal financial aid application closes',
        due_date: '2026-06-30',
        category: 'financial_aid',
        deadline_type: 'fafsa_close'
      }
    ];

    try {
      // First check if any global deadlines already exist
      const { data: existingDeadlines, error: checkError } = await supabase
        .from('college_journey_global_deadlines')
        .select('id')
        .limit(1);

      if (checkError) {
        console.warn('⚠️ Cannot access global deadlines table:', checkError.message);
        // Return empty array instead of throwing - this is not critical
        return [];
      }

      if (existingDeadlines && existingDeadlines.length > 0) {
        console.log('ℹ️ Global deadlines already exist, skipping seeding');
        return [];
      }

      const { data, error } = await supabase
        .from('college_journey_global_deadlines')
        .insert(
          defaultDeadlines.map(deadline => ({
            ...deadline,
            user_id: userId
          }))
        )
        .select();

      if (error) {
        // If it's an RLS error, don't throw - just warn and continue
        if (error.code === '42501') {
          console.warn('⚠️ RLS policy prevents seeding global deadlines. This is non-critical.');
          return [];
        }
        throw error;
      }
      
      console.log('✅ Seeded', data?.length || 0, 'global deadlines');
      return data;
    } catch (error) {
      console.error('Error seeding default global deadlines:', error);
      // Don't re-throw - this is non-critical functionality
      console.warn('⚠️ Global deadlines seeding failed, but application will continue');
      return [];
    }
  }
}; 